// react
import {
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';

// types
import {
	Category,
	Component,
	ExpenseType,
	PlanningActivity,
	V2Contract,
	WorkPlan,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// mui
import {
	alpha,
	Box,
	Button,
	Chip,
	Collapse,
	IconButton,
	LinearProgress,
	Stack,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	Add as IconAdd,
	Category as IconCategory,
	ChevronRight as IconChevronRight,
	ExpandMore as IconExpandMore,
	Extension as IconComponent,
	Assignment as IconWorkPlan,
	HelpOutline as IconUnassigned,
	PlaylistAdd as IconAssign,
	Receipt as IconExpenseType,
	SubdirectoryArrowRight as IconSubActivity,
	UnfoldLess as IconUnfoldLess,
	UnfoldMore as IconUnfoldMore,
	AddCircleOutline as IconAddExisting,
} from '@mui/icons-material';

// local
import { getStatusColor } from '../HeaderDetailPage/HeaderDetailPage';
import { COLORS, GRADIENTS } from '../../../data';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';

// Types

/** A single grouping bucket: something with an id, name, and status */
export interface GroupItem {
	id: number;
	name: string;
	status?: string;
}

export type GroupByKey = 'component' | 'category' | 'expenseType' | 'workPlan';

export interface GroupByTab {
	/** Which activity field to group on */
	key: GroupByKey;
	/** Tab label, e.g. "By Component" */
	label: string;
	/** The reference items (components/categories/etc). When supplied the list shows
	 *  cards even when no activities match. When omitted only buckets with activities appear. */
	items?: GroupItem[];
}

export interface ActivityGroupedListProps {
	/** Tabs to show (at least one) */
	tabs: GroupByTab[];
	/** Optional override: use these activities instead of ContextSpendPlan.activities */
	activities?: PlanningActivity[];
	/** Navigate to an activity detail page */
	onNavigateActivity?: (activityId: number) => void;
	/** Add a brand-new activity for this group item */
	onAddActivity?: (key: GroupByKey, itemId: number) => void;
	/** Assign existing (unassigned) activities to this group item */
	onAssignExisting?: (key: GroupByKey, itemId: number, itemName: string) => void;
	/** Add an existing activity (not yet in any work plan) to this work plan */
	onAddExistingToWorkPlan?: () => void;
	/** Count of activities available to add (not yet in any work plan) */
	availableActivityCount?: number;
	/** Lookup contracts for an activity */
	getContractsByActivity?: (activityId: number) => V2Contract[];
	/** Navigate to a contract detail page */
	onNavigateContract?: (contractId: number) => void;
	/** Project local-currency code (e.g. 'ETB'); enables local-equivalent sub-lines */
	projectLocalCurrency?: string | null;
	/** Called when the active tab changes */
	onTabChange?: (key: GroupByKey) => void;
	/** Auto-expand all groups on first render (default: true) */
	autoExpand?: boolean;
	/** Externally controlled default expand state — overrides autoExpand when provided */
	defaultExpanded?: boolean;
	/** Show the Expand All / Collapse All toggle (default: true) */
	showExpandToggle?: boolean;
	/** Called when expand state changes (for external controls) */
	onExpandStateChange?: (state: { allExpanded: boolean; hasExpandable: boolean }) => void;
}

export interface ActivityGroupedListHandle {
	toggleAll: () => void;
}

// Constants – per-key accent colours & icons

/** Sentinel ID used for the "Unassigned" expand/collapse state */
const UNASSIGNED_ID = -1;

const GROUP_META: Record<GroupByKey, { color: string; icon: React.ReactNode }> = {
	component: {
		color: COLORS.primary.main,
		icon: <IconComponent sx={{ fontSize: 22 }} />,
	},
	category: {
		color: COLORS.info.main,
		icon: <IconCategory sx={{ fontSize: 22 }} />,
	},
	expenseType: {
		color: '#8b5cf6',
		icon: <IconExpenseType sx={{ fontSize: 22 }} />,
	},
	workPlan: {
		color: COLORS.warning.main,
		icon: <IconWorkPlan sx={{ fontSize: 22 }} />,
	},
};

// Helpers

/** Extract the numeric id and the { id, name, status? } object from a
 *  polymorphic activity field such as `activity.component` which may be a
 *  number, a populated object, or null. */
const resolveRef = (
	ref: number | { id: number; name: string; status?: string } | null
): { id: number; obj: GroupItem } | null => {
	if (ref === null || ref === undefined) return null;
	if (typeof ref === 'number') return { id: ref, obj: { id: ref, name: `#${ref}` } };
	return {
		id: ref.id,
		obj: { id: ref.id, name: ref.name, status: (ref as { status?: string }).status },
	};
};

/** For a given groupBy key return the activity field value */
const getActivityRef = (
	activity: PlanningActivity,
	key: GroupByKey
): number | Category | Component | ExpenseType | WorkPlan | null => {
	switch (key) {
		case 'component':
			return activity.component;
		case 'category':
			return activity.category;
		case 'expenseType':
			return activity.expenseType;
		case 'workPlan':
			return activity.workPlan;
	}
};

// Contract Row — white bg, high-contrast text, colored accents only

const ContractRow = ({
	contract,
	activityCurrency,
	onNavigateContract,
	projectLocalCurrency,
}: {
	contract: V2Contract;
	activityCurrency?: string;
	onNavigateContract?: (contractId: number) => void;
	projectLocalCurrency?: string | null;
}) => {
	// Prefer the commitment amount (in the activity's work-plan currency).
	// If no commitment is set the full contract amount is implicitly committed
	// to the activity, so fall back to the contract's total in that case.
	const fallbackTotal = contract.amounts?.reduce((sum, a) => sum + (a.amount || 0), 0) || 0;
	const displayAmount = contract.commitmentAmount ?? fallbackTotal;
	const currency = activityCurrency || contract.amounts?.[0]?.currency || 'USD';
	// Local equivalent is the nominal local commitment captured on the record;
	// shown only when both the commitment and its local figure are present.
	const localAmount =
		projectLocalCurrency && contract.commitmentAmount != null
			? contract.commitmentAmountLocal
			: null;

	return (
		<Box
			onClick={(e) => {
				e.stopPropagation();
				onNavigateContract?.(contract.id);
			}}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 2,
				pl: 8.5,
				pr: 2.5,
				py: 1.25,
				minWidth: 850,
				cursor: onNavigateContract ? 'pointer' : 'default',
				bgcolor: alpha(GRADIENTS.contract.icon, 0.03),
				transition: 'background-color 0.15s',
				'&:hover': onNavigateContract ? { bgcolor: alpha(GRADIENTS.contract.icon, 0.08) } : {},
				'&:focus-visible': { outline: `2px solid ${GRADIENTS.contract.icon}`, outlineOffset: -2 },
				'& + &': { borderTop: '1px solid #f0f0f0' },
			}}>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					sx={{
						fontSize: 13,
						fontWeight: 600,
						color: onNavigateContract ? GRADIENTS.contract.icon : '#111827',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}>
					{contract.description ||
						contract.num ||
						`Contract #${contract.id}`}
				</Typography>
			</Box>

			<Box sx={{ width: 110, flexShrink: 0 }}>
				<Typography
					sx={{
						fontSize: 13,
						fontWeight: 600,
						fontFeatureSettings: '"tnum"',
						color: '#111827',
						textAlign: 'right',
					}}>
					{formatCurrency({ value: displayAmount, currency, decimals: 0 })}
				</Typography>
				{localAmount != null && projectLocalCurrency && (
					<Typography
						sx={{
							fontSize: 12,
							fontFeatureSettings: '"tnum"',
							color: '#6b7280',
							textAlign: 'right',
						}}>
						{formatCurrency({ value: localAmount, currency: projectLocalCurrency, decimals: 0 })}
					</Typography>
				)}
			</Box>

			<Typography
				sx={{
					fontSize: 12,
					color: '#374151',
					width: 180,
					textAlign: 'right',
					flexShrink: 0,
				}}>
				{contract.dateSigning
					? new Date(contract.dateSigning).toLocaleDateString('en-US', {
							timeZone: 'UTC',
							month: 'short',
							day: 'numeric',
							year: 'numeric',
						})
					: '—'}
			</Typography>

			<Box sx={{ width: 90, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
				<Chip
					label={contract.status?.replace(/_/g, ' ') || 'ACTIVE'}
					size="small"
					sx={{
						bgcolor:
							contract.status === 'ACTIVE'
								? alpha(COLORS.success.main, 0.1)
								: alpha('#6b7280', 0.1),
						color: contract.status === 'ACTIVE' ? COLORS.success.main : '#374151',
						fontWeight: 600,
						fontSize: 11,
						height: 24,
						minWidth: 70,
					}}
				/>
			</Box>
		</Box>
	);
};

// Activity Row — white bg, no gray, high-contrast text

const ActivityRow = ({
	activity,
	depth = 0,
	index = 0,
	getContractsByActivity,
	onNavigate,
	onNavigateContract,
	projectLocalCurrency,
}: {
	activity: PlanningActivity;
	depth?: number;
	index?: number;
	getContractsByActivity?: (activityId: number) => V2Contract[];
	onNavigate?: (id: number) => void;
	onNavigateContract?: (contractId: number) => void;
	projectLocalCurrency?: string | null;
}) => {
	const [contractsOpen, setContractsOpen] = useState(false);
	const statusColors = getStatusColor(activity.status);
	const workPlanName =
		activity.workPlan && typeof activity.workPlan === 'object' ? activity.workPlan.name : null;
	const contracts = getContractsByActivity?.(activity.id) || [];
	const children = activity.children || [];
	const hasContracts = contracts.length > 0;
	const stripe = depth === 0 && index % 2 === 1;

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 2,
					px: 2.5,
					pl: 2.5 + depth * 3,
					py: 1.5,
					minWidth: 850,
					bgcolor: stripe
						? alpha(GRADIENTS.activity.icon, 0.04)
						: alpha(GRADIENTS.activity.icon, 0.02),
					borderBottom: '1px solid #e5e7eb',
					transition: 'background-color 0.15s',
					'&:hover': { bgcolor: alpha(GRADIENTS.activity.icon, 0.08) },
					'&:focus-within': { bgcolor: alpha(GRADIENTS.activity.icon, 0.08) },
				}}>
				{/* Expand chevron: for contracts toggle */}
				<Box
					sx={{
						width: 24,
						flexShrink: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}>
					{hasContracts ? (
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								setContractsOpen((prev) => !prev);
							}}
							sx={{ p: 0 }}>
							{contractsOpen ? (
								<IconExpandMore sx={{ fontSize: 20, color: COLORS.primary.main }} />
							) : (
								<IconChevronRight sx={{ fontSize: 20, color: '#9ca3af' }} />
							)}
						</IconButton>
					) : depth > 0 ? (
						<IconSubActivity sx={{ color: '#c4c4c4', fontSize: 16 }} />
					) : null}
				</Box>

				<Box
					onClick={() => onNavigate?.(activity.id)}
					sx={{
						bgcolor: alpha(GRADIENTS.activity.icon, 0.1),
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						height: 32,
						width: 32,
						minWidth: 32,
						borderRadius: '50%',
						flexShrink: 0,
						cursor: onNavigate ? 'pointer' : 'default',
					}}>
					<IconActivity sx={{ color: GRADIENTS.activity.icon, fontSize: 16, display: 'block' }} />
				</Box>

				<Box
					onClick={() => onNavigate?.(activity.id)}
					sx={{ flex: 1, minWidth: 0, cursor: onNavigate ? 'pointer' : 'default' }}>
					<Typography
						sx={{
							fontSize: 14,
							fontWeight: 600,
							color: onNavigate ? GRADIENTS.activity.icon : '#111827',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							textDecoration: 'underline',
							textDecorationColor: 'transparent',
							textUnderlineOffset: 2,
							'&:hover': onNavigate ? { textDecorationColor: GRADIENTS.activity.icon } : {},
						}}>
						{activity.name}
					</Typography>
					{workPlanName && (
						<Typography noWrap sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
							{workPlanName}
						</Typography>
					)}
				</Box>

				<Tooltip title={`${activity.progress}% complete`} arrow>
					<Stack direction="row" alignItems="center" spacing={1} sx={{ width: 130, flexShrink: 0 }}>
						<LinearProgress
							variant="determinate"
							value={activity.progress}
							sx={{
								flex: 1,
								height: 8,
								borderRadius: 4,
								bgcolor: '#e2e5ea',
								'& .MuiLinearProgress-bar': {
									borderRadius: 4,
									bgcolor:
										activity.progress >= 100
											? COLORS.success.main
											: activity.progress > 0
												? COLORS.primary.main
												: '#e5e7eb',
								},
							}}
						/>
						<Typography
							sx={{
								fontSize: 12,
								fontWeight: 600,
								color: '#111827',
								minWidth: 32,
								textAlign: 'right',
							}}>
							{activity.progress}%
						</Typography>
					</Stack>
				</Tooltip>

				<Box sx={{ width: 110, flexShrink: 0 }}>
					<Typography
						sx={{
							fontSize: 13,
							fontWeight: 600,
							fontFeatureSettings: '"tnum"',
							color: '#111827',
							textAlign: 'right',
						}}>
						{formatCurrency({ value: activity.amount, currency: activity.currency, decimals: 0 })}
					</Typography>
					{activity.amountLocal != null && activity.currencyLocal && (
						<Typography
							sx={{
								fontSize: 12,
								fontFeatureSettings: '"tnum"',
								color: '#6b7280',
								textAlign: 'right',
							}}>
							{formatCurrency({
								value: activity.amountLocal,
								currency: activity.currencyLocal,
								decimals: 0,
							})}
						</Typography>
					)}
				</Box>

				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 500,
						color: '#374151',
						width: 180,
						textAlign: 'right',
						flexShrink: 0,
					}}>
					{formatDate({ date: activity.dateStart })} – {formatDate({ date: activity.dateEnd })}
				</Typography>

				<Box sx={{ width: 90, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
					<Chip
						label={activity.status.replace(/_/g, ' ')}
						size="small"
						sx={{
							bgcolor: statusColors.bg,
							color: statusColors.text,
							fontWeight: 600,
							fontSize: 11,
							height: 24,
							minWidth: 80,
						}}
					/>
				</Box>
			</Box>

			{/* Contracts — blue tinted background, blue left accent border for hierarchy */}
			{hasContracts && (
				<Collapse in={contractsOpen}>
					<Box
						sx={{
							bgcolor: alpha(GRADIENTS.contract.icon, 0.02),
							borderLeft: `3px solid ${alpha(GRADIENTS.contract.icon, 0.5)}`,
							ml: 5 + depth * 3,
							borderBottom: '1px solid #e5e7eb',
						}}>
						{/* "Contracts" label row */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								pl: 8.5,
								pr: 2.5,
								py: 0.75,
								borderBottom: '1px solid #f0f0f0',
							}}>
							<Typography
								sx={{
									fontSize: 12,
									fontWeight: 700,
									color: '#374151',
									letterSpacing: 0.3,
								}}>
								Contracts ({contracts.length})
							</Typography>
						</Box>
						{contracts.map((contract) => (
							<ContractRow
								key={contract.id}
								contract={contract}
								activityCurrency={activity.currency}
								onNavigateContract={onNavigateContract}
								projectLocalCurrency={projectLocalCurrency}
							/>
						))}
					</Box>
				</Collapse>
			)}

			{/* Sub-activities (recursive) */}
			{children.map((child, childIdx) => (
				<ActivityRow
					key={child.id}
					activity={child}
					depth={depth + 1}
					index={childIdx}
					getContractsByActivity={getContractsByActivity}
					onNavigate={onNavigate}
					onNavigateContract={onNavigateContract}
					projectLocalCurrency={projectLocalCurrency}
				/>
			))}
		</>
	);
};

// Expandable Card

const ExpandableGroupCard = ({
	accentColor,
	activities: cardActivities,
	expanded,
	getContractsByActivity,
	icon,
	itemName,
	onAddActivity,
	onAssignExisting,
	onNavigateActivity,
	onNavigateContract,
	onToggleExpanded,
	status,
	unassignedCount,
	projectLocalCurrency,
}: {
	accentColor: string;
	activities: PlanningActivity[];
	expanded: boolean;
	getContractsByActivity?: (activityId: number) => V2Contract[];
	icon: React.ReactNode;
	itemName: string;
	onAddActivity?: () => void;
	onAssignExisting?: () => void;
	onNavigateActivity?: (id: number) => void;
	onNavigateContract?: (contractId: number) => void;
	onToggleExpanded: () => void;
	status?: string;
	unassignedCount: number;
	projectLocalCurrency?: string | null;
}) => {
	const { t } = useTranslation();
	const count = cardActivities.length;
	const totalAmount = cardActivities.reduce((sum, a) => sum + (a.amount || 0), 0);
	const currency = cardActivities[0]?.currency || 'USD';
	const hasActivities = count > 0;
	// Sum the nominal local budgets captured on each activity. Shown only when a
	// project local currency exists and the activities carry local figures.
	const totalAmountLocal = cardActivities.reduce((sum, a) => sum + (a.amountLocal || 0), 0);
	const localCurrency =
		projectLocalCurrency || cardActivities.find((a) => a.currencyLocal)?.currencyLocal || null;

	return (
		<Box
			sx={{
				border: '1px solid',
				borderColor: expanded ? alpha(accentColor, 0.3) : '#e5e7eb',
				borderLeft: `4px solid ${accentColor}`,
				borderRadius: 2,
				overflow: 'hidden',
				transition: 'border-color 0.2s',
			}}>
			{/* Header */}
			<Box
				onClick={() => hasActivities && onToggleExpanded()}
				sx={{
					display: 'flex',
					alignItems: 'center',
					flexWrap: 'wrap',
					gap: 1.5,
					p: 2.5,
					cursor: hasActivities ? 'pointer' : 'default',
					transition: 'background-color 0.15s',
					'&:hover': hasActivities ? { bgcolor: '#f5f7ff' } : {},
				}}>
				{/* Identity row: chevron + icon + name */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 200 }}>
					{hasActivities ? (
						expanded ? (
							<IconExpandMore sx={{ fontSize: 20, color: accentColor, flexShrink: 0 }} />
						) : (
							<IconChevronRight sx={{ fontSize: 20, color: '#9ca3af', flexShrink: 0 }} />
						)
					) : (
						<Box sx={{ width: 20, flexShrink: 0 }} />
					)}

					<Box
						sx={{
							bgcolor: alpha(accentColor, 0.1),
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: 44,
							width: 44,
							minWidth: 44,
							borderRadius: '50%',
							flexShrink: 0,
						}}>
						{icon}
					</Box>

					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="body1" fontWeight={600} noWrap>
							{itemName}
						</Typography>
						{hasActivities && totalAmount > 0 && (
							<Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.25 }}>
								Total: {formatCurrency({ value: totalAmount, currency, decimals: 0 })}
								{localCurrency && totalAmountLocal > 0 && (
									<Box component="span" sx={{ color: '#9ca3af' }}>
										{' · '}
										{formatCurrency({ value: totalAmountLocal, currency: localCurrency, decimals: 0 })}
									</Box>
								)}
							</Typography>
						)}
					</Box>
				</Box>

				{/* Actions + metadata */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
					{unassignedCount > 0 && onAssignExisting && (
						<Tooltip title={`Assign ${unassignedCount} unassigned activities`} arrow>
							<IconButton
								onClick={(e) => {
									e.stopPropagation();
									onAssignExisting();
								}}
								size="small"
								sx={{
									bgcolor: alpha(COLORS.warning.main, 0.1),
									color: COLORS.warning.main,
									flexShrink: 0,
									'&:hover': { bgcolor: alpha(COLORS.warning.main, 0.2) },
								}}>
								<IconAssign sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
					)}

					{onAddActivity && (
						<Tooltip title={t('spendPlan.addActivity')} arrow>
							<IconButton
								onClick={(e) => {
									e.stopPropagation();
									onAddActivity();
								}}
								size="small"
								sx={{
									bgcolor: alpha(accentColor, 0.1),
									color: accentColor,
									flexShrink: 0,
									'&:hover': { bgcolor: alpha(accentColor, 0.2) },
								}}>
								<IconAdd sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
					)}

					<Chip
						label={t('spendPlan.activities.activityCount', { count })}
						size="small"
						sx={{
							bgcolor: alpha(COLORS.info.main, 0.1),
							color: COLORS.info.main,
							fontWeight: 600,
							width: 100,
							flexShrink: 0,
							justifyContent: 'center',
						}}
					/>

					{status && (
						<Chip
							label={status}
							size="small"
							sx={{
								bgcolor: status === 'ACTIVE' ? alpha(COLORS.success.main, 0.1) : alpha('#666', 0.1),
								color: status === 'ACTIVE' ? COLORS.success.main : '#666',
								fontWeight: 600,
								fontSize: 12,
								width: 80,
								flexShrink: 0,
								justifyContent: 'center',
							}}
						/>
					)}
				</Box>
			</Box>

			{/* Expandable activities list */}
			<Collapse in={expanded}>
				{hasActivities && (
					<Box
						sx={{
							borderTop: '1px solid',
							borderColor: '#e5e7eb',
							overflowX: 'auto',
						}}>
						{/* Column headers */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
								px: 2.5,
								py: 1,
								borderBottom: '2px solid',
								borderColor: '#e5e7eb',
								minWidth: 850,
							}}>
							<Box sx={{ width: 24 }} /> {/* chevron spacer */}
							<Box sx={{ width: 32 }} /> {/* avatar spacer */}
							{[
								{ label: t('activity'), flex: 1, width: undefined, align: 'left' as const },
								{ label: t('spendPlan.progress'), flex: undefined, width: 130, align: 'center' as const },
								{ label: t('amount'), flex: undefined, width: 110, align: 'right' as const },
								{ label: t('dates'), flex: undefined, width: 180, align: 'right' as const },
								{ label: t('status'), flex: undefined, width: 90, align: 'center' as const },
							].map((col) => (
								<Typography
									key={col.label}
									sx={{
										flex: col.flex,
										width: col.width,
										flexShrink: col.flex ? undefined : 0,
										fontSize: 11,
										fontWeight: 700,
										color: '#9ca3af',
										letterSpacing: 0.5,
										textTransform: 'uppercase',
										textAlign: col.align,
									}}>
									{col.label}
								</Typography>
							))}
						</Box>
						{cardActivities.map((activity, idx) => (
							<ActivityRow
								key={activity.id}
								activity={activity}
								index={idx}
								getContractsByActivity={getContractsByActivity}
								onNavigate={onNavigateActivity}
								onNavigateContract={onNavigateContract}
								projectLocalCurrency={projectLocalCurrency}
							/>
						))}
					</Box>
				)}
			</Collapse>
		</Box>
	);
};

// Main Component

export const ActivityGroupedList = forwardRef<ActivityGroupedListHandle, ActivityGroupedListProps>(
	(
		{
			tabs,
			activities: activitiesProp,
			onNavigateActivity,
			onNavigateContract,
			onAddActivity,
			onAssignExisting,
			onAddExistingToWorkPlan,
			availableActivityCount,
			getContractsByActivity,
			onTabChange,
			autoExpand = true,
			defaultExpanded,
			showExpandToggle = true,
			onExpandStateChange,
			projectLocalCurrency,
		},
		ref
	) => {
		const { t } = useTranslation();
		// Use prop activities if provided, otherwise fall back to shared SpendPlan context
		const { activities: contextActivities, loading } = useContext(ContextSpendPlan);
		const activities = activitiesProp ?? contextActivities;

		const [activeTab, setActiveTab] = useState(0);
		const [expanded, setExpanded] = useState<Set<number>>(new Set());
		// Track user's explicit expand/collapse intent across tab switches
		const [userExpandIntent, setUserExpandIntent] = useState<'all' | 'none' | null>(
			defaultExpanded !== undefined ? (defaultExpanded ? 'all' : 'none') : null
		);

		const currentTab = tabs[activeTab] || tabs[0];
		const currentTabKey = currentTab.key;
		const currentTabItems = currentTab.items;

		// Build the grouped data for the current tab
		const { groups, unassignedActivities } = useMemo(() => {
			const bucketMap = new Map<number, { item: GroupItem; activities: PlanningActivity[] }>();

			// Seed from explicit items if provided
			if (currentTabItems) {
				for (const item of currentTabItems) {
					bucketMap.set(item.id, { item, activities: [] });
				}
			}

			// Distribute activities into buckets
			const unassignedList: PlanningActivity[] = [];
			for (const a of activities) {
				const ref = resolveRef(
					getActivityRef(a, currentTabKey) as Parameters<typeof resolveRef>[0]
				);
				if (!ref) {
					unassignedList.push(a);
					continue;
				}
				const existing = bucketMap.get(ref.id);
				if (existing) {
					existing.activities.push(a);
				} else {
					bucketMap.set(ref.id, { item: ref.obj, activities: [a] });
				}
			}

			// Sort by name
			const sorted = Array.from(bucketMap.values()).sort((a, b) =>
				a.item.name.localeCompare(b.item.name)
			);

			return { groups: sorted, unassignedActivities: unassignedList };
		}, [activities, currentTabKey, currentTabItems]);

		// Auto-expand IDs for current tab (include unassigned sentinel when applicable)
		const expandableIds = useMemo(() => {
			const ids = groups.filter((g) => g.activities.length > 0).map((g) => g.item.id);
			if (unassignedActivities.length > 0) ids.push(UNASSIGNED_ID);
			return ids;
		}, [groups, unassignedActivities]);

		// Reset expansion when tab changes, respecting user's last expand/collapse choice
		useEffect(() => {
			const intent = userExpandIntent ?? (autoExpand ? 'all' : 'none');
			if (intent === 'all') {
				setExpanded(new Set(expandableIds));
			} else {
				setExpanded(new Set());
			}
		}, [activeTab, expandableIds, autoExpand, userExpandIntent]);

		const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));

		const toggleItem = useCallback((id: number) => {
			setExpanded((prev) => {
				const next = new Set(prev);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
		}, []);

		const toggleAll = useCallback(() => {
			if (allExpanded) {
				setExpanded(new Set());
				setUserExpandIntent('none');
			} else {
				setExpanded(new Set(expandableIds));
				setUserExpandIntent('all');
			}
		}, [allExpanded, expandableIds]);

		useImperativeHandle(
			ref,
			() => ({
				toggleAll,
			}),
			[toggleAll]
		);

		// Notify parent of expand state changes (including initial mount)
		useEffect(() => {
			if (onExpandStateChange) {
				onExpandStateChange({ allExpanded, hasExpandable: expandableIds.length > 0 });
			}
		}, [allExpanded, expandableIds, onExpandStateChange]);

		const meta = GROUP_META[currentTab.key];

		if (loading.activities && activities.length === 0) {
			return (
				<Box sx={{ py: 4 }}>
					<LinearProgress />
				</Box>
			);
		}

		return (
			<Box>
				{/* Tabs + Expand toggle */}
				{tabs.length > 1 && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							borderBottom: '1px solid',
							borderColor: 'divider',
							mb: 3,
						}}>
						<Tabs
							value={activeTab}
							onChange={(_, v) => {
								setActiveTab(v);
								if (tabs[v]) onTabChange?.(tabs[v].key);
							}}
							variant="standard"
							sx={{ flex: 1, minHeight: 40 }}>
							{tabs.map((t, i) => {
								const tabMeta = GROUP_META[t.key];
								return (
									<Tab
										key={t.key}
										icon={tabMeta.icon as React.ReactElement}
										iconPosition="start"
										label={t.label}
										value={i}
										sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 }}
									/>
								);
							})}
						</Tabs>
						{showExpandToggle && expandableIds.length > 0 && (
							<Button
								onClick={toggleAll}
								size="small"
								startIcon={allExpanded ? <IconUnfoldLess /> : <IconUnfoldMore />}
								sx={{
									textTransform: 'none',
									fontWeight: 600,
									color: '#6b7280',
									whiteSpace: 'nowrap',
									flexShrink: 0,
									ml: 'auto',
								}}>
								{allExpanded ? t('collapseAll') : t('expandAll')}
							</Button>
						)}
						{onAddExistingToWorkPlan && (
							<Tooltip
								title={
									availableActivityCount
										? t('spendPlan.activities.unassignedAvailable', { count: availableActivityCount })
										: t('spendPlan.activities.addExistingToWorkPlan')
								}
								arrow>
								<Button
									onClick={onAddExistingToWorkPlan}
									size="small"
									startIcon={<IconAddExisting />}
									variant="outlined"
									sx={{
										textTransform: 'none',
										fontWeight: 600,
										whiteSpace: 'nowrap',
										flexShrink: 0,
										ml: expandableIds.length > 0 ? 1 : 'auto',
										borderColor: GRADIENTS.activity.icon,
										color: GRADIENTS.activity.icon,
										'&:hover': {
											bgcolor: alpha(GRADIENTS.activity.icon, 0.08),
											borderColor: GRADIENTS.activity.icon,
										},
									}}>
									{t('spendPlan.activities.addExisting')}
								</Button>
							</Tooltip>
						)}
					</Box>
				)}

				{/* Single-tab header (no tabs bar, just expand toggle) */}
				{tabs.length === 1 && (showExpandToggle || onAddExistingToWorkPlan) && (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
						{showExpandToggle && expandableIds.length > 0 && (
							<Button
								onClick={toggleAll}
								size="small"
								startIcon={allExpanded ? <IconUnfoldLess /> : <IconUnfoldMore />}
								sx={{
									textTransform: 'none',
									fontWeight: 600,
									color: '#6b7280',
									whiteSpace: 'nowrap',
								}}>
								{allExpanded ? t('collapseAll') : t('expandAll')}
							</Button>
						)}
						{onAddExistingToWorkPlan && (
							<Button
								onClick={onAddExistingToWorkPlan}
								size="small"
								startIcon={<IconAddExisting />}
								variant="outlined"
								sx={{
									textTransform: 'none',
									fontWeight: 600,
									whiteSpace: 'nowrap',
									borderColor: GRADIENTS.activity.icon,
									color: GRADIENTS.activity.icon,
									'&:hover': {
										bgcolor: alpha(GRADIENTS.activity.icon, 0.08),
										borderColor: GRADIENTS.activity.icon,
									},
								}}>
								{t('spendPlan.activities.addExisting')}
							</Button>
						)}
					</Box>
				)}

				{/* Cards — with horizontal padding to match page content */}
				<Box sx={{ px: 3 }}>
					{groups.length === 0 && unassignedActivities.length === 0 ? (
						<Box
							sx={{
								py: 6,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								textAlign: 'center',
							}}>
							<IconActivity sx={{ fontSize: 64, color: '#d1d5db', mb: 1.5 }} />
							<Typography color="text.secondary" sx={{ fontSize: '1.25rem', mb: 0.5 }}>
								{t('spendPlan.activities.noActivitiesInWorkPlan')}
							</Typography>
							<Typography color="text.secondary" sx={{ fontSize: '1.1rem', mb: 2 }}>
								{t('spendPlan.activities.groupedByHint', { groupBy: currentTab.label.replace(/^By /, '').toLowerCase() })}
							</Typography>
							{onAddActivity && (
								<Button
									variant="contained"
									startIcon={<IconAdd />}
									onClick={() => onAddActivity(currentTab.key, 0)}
									sx={{ textTransform: 'none', fontWeight: 600 }}>
									{t('spendPlan.addActivity')}
								</Button>
							)}
							{onAddExistingToWorkPlan && (
								<Button
									variant="outlined"
									startIcon={<IconAddExisting />}
									onClick={onAddExistingToWorkPlan}
									sx={{
										textTransform: 'none',
										fontWeight: 600,
										ml: 1,
										borderColor: GRADIENTS.activity.icon,
										color: GRADIENTS.activity.icon,
										'&:hover': {
											bgcolor: alpha(GRADIENTS.activity.icon, 0.08),
											borderColor: GRADIENTS.activity.icon,
										},
									}}>
									{t('spendPlan.activities.addExistingActivity')}
								</Button>
							)}
						</Box>
					) : (
						<Stack spacing={2}>
							{groups.map((group) => (
								<ExpandableGroupCard
									key={group.item.id}
									accentColor={meta.color}
									activities={group.activities}
									expanded={expanded.has(group.item.id)}
									icon={<Box sx={{ color: meta.color, display: 'flex' }}>{meta.icon}</Box>}
									itemName={group.item.name}
									onAddActivity={
										onAddActivity ? () => onAddActivity(currentTab.key, group.item.id) : undefined
									}
									onAssignExisting={
										onAssignExisting
											? () => onAssignExisting(currentTab.key, group.item.id, group.item.name)
											: undefined
									}
									onNavigateActivity={onNavigateActivity}
									onNavigateContract={onNavigateContract}
									onToggleExpanded={() => toggleItem(group.item.id)}
									getContractsByActivity={getContractsByActivity}
									status={group.item.status}
									unassignedCount={unassignedActivities.length}
									projectLocalCurrency={projectLocalCurrency}
								/>
							))}
							{/* Unassigned activities — shown as the final card */}
							{unassignedActivities.length > 0 && (
								<ExpandableGroupCard
									key="unassigned"
									accentColor="#9ca3af"
									activities={unassignedActivities}
									expanded={expanded.has(UNASSIGNED_ID)}
									icon={
										<Box sx={{ color: '#9ca3af', display: 'flex' }}>
											<IconUnassigned sx={{ fontSize: 22 }} />
										</Box>
									}
									itemName={t('spendPlan.financeOverview.uncategorized')}
									onNavigateActivity={onNavigateActivity}
									onNavigateContract={onNavigateContract}
									onToggleExpanded={() => toggleItem(UNASSIGNED_ID)}
									getContractsByActivity={getContractsByActivity}
									unassignedCount={0}
									projectLocalCurrency={projectLocalCurrency}
								/>
							)}
						</Stack>
					)}
				</Box>
			</Box>
		);
	}
);
